import { z } from 'zod';
import { hexColorSchema, hillPositionSchema } from './primitives.js';

/**
 * HTTP request bodies shared between backend (validation) and frontend (forms).
 */

// ── POST /api/hills ──────────────────────────────────────────────────────────
// `title` is the pitch name, typed by the user during import. It is required
// on purpose: a fallback to the upstream project name would leave the field
// skippable through the API while the UI makes it mandatory, and would keep
// two ways of naming a hill alive. The bound mirrors `updateHillBodySchema` -
// both routes write the same column, so they enforce the same rule.
export const createHillChartBodySchema = z.object({
  externalProjectId: z.string().min(1),
  title: z.string().trim().min(1).max(255),
});
export type CreateHillChartBody = z.infer<typeof createHillChartBodySchema>;

// ── PATCH /api/hills/:id/dots/:dotId ─────────────────────────────────────────
// Atomic mutation of one or more of: horizontal position (`x`), color, or
// completion status. At least one field must be provided — an empty body is
// rejected with 400 so the frontend doesn't accidentally issue a noop.
//
// Completion can be set from the UI (e.g. a "mark done" button). Resync is
// the other writer of that field: it realigns `completed` with the upstream
// provider, so a value forced here holds until the next resync.
export const updateDotBodySchema = z
  .object({
    x: hillPositionSchema.optional(),
    color: hexColorSchema.optional(),
    completed: z.boolean().optional(),
  })
  .refine((v) => v.x !== undefined || v.color !== undefined || v.completed !== undefined, {
    message: 'Provide at least one of: x, color, completed.',
  });
export type UpdateDotBody = z.infer<typeof updateDotBodySchema>;

// ── PATCH /api/hills/:id ─────────────────────────────────────────────────────
// Rename the hill. `title` is user-owned: it is seeded from the upstream
// project name at import time, and resync never touches it afterwards. The
// upstream name keeps flowing into `externalProjectName`, so the two can
// diverge on purpose.
export const updateHillBodySchema = z.object({
  title: z.string().trim().min(1).max(255),
});
export type UpdateHillBody = z.infer<typeof updateHillBodySchema>;

// ── POST /api/hills/:id/snapshots ────────────────────────────────────────────
export const createSnapshotBodySchema = z.object({
  note: z.string().max(500).optional(),
});
export type CreateSnapshotBody = z.infer<typeof createSnapshotBodySchema>;
